import type { BootstrapResponse, Permission, SessionUser, StoreInfo } from '@multicrm/shared';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface SessionContextValue {
  user: SessionUser;
  stores: StoreInfo[];
  /** выбранные магазины; пустой массив — все */
  selectedStores: string[];
  toggleStore: (storeId: string) => void;
  selectOnlyStore: (storeId: string) => void;
  selectAllStores: () => void;
  storeById: (storeId: string) => StoreInfo | undefined;
  /** магазины, по которым реально идёт работа (с учётом выбора) */
  activeStores: StoreInfo[];
  can: (permission: Permission) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function getStorageKey(telegramId?: number): string {
  return telegramId ? `multicrm.stores.${telegramId}` : 'multicrm.stores';
}

function readSelection(available: string[], telegramId?: number): string[] {
  try {
    const key = getStorageKey(telegramId);
    let stored = window.localStorage.getItem(key);
    if (!stored && telegramId) {
      stored = window.localStorage.getItem('multicrm.stores');
    }
    if (!stored) return [];
    const parsed = JSON.parse(stored) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Магазин могли отключить на сервере — выбор не должен его «помнить».
    return parsed.filter((id): id is string => typeof id === 'string' && available.includes(id));
  } catch {
    return [];
  }
}

export function SessionProvider({
  bootstrap,
  children,
}: {
  bootstrap: BootstrapResponse;
  children: ReactNode;
}): ReactNode {
  const available = useMemo(() => bootstrap.stores.map((store) => store.id), [bootstrap.stores]);
  const storageKey = useMemo(
    () => getStorageKey(bootstrap.user.telegramId),
    [bootstrap.user.telegramId],
  );
  const [selectedStores, setSelected] = useState<string[]>(() =>
    readSelection(available, bootstrap.user.telegramId),
  );

  const persist = useCallback(
    (next: string[]) => {
      setSelected(next);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // не критично: выбор действует до перезапуска
      }
    },
    [storageKey],
  );

  const toggleStore = useCallback(
    (storeId: string) => {
      const next = selectedStores.includes(storeId)
        ? selectedStores.filter((id) => id !== storeId)
        : [...selectedStores, storeId];
      // Выбрать все вручную — то же самое, что не выбирать ничего.
      persist(next.length === available.length ? [] : next);
    },
    [selectedStores, available.length, persist],
  );

  const selectOnlyStore = useCallback(
    (storeId: string) => {
      persist([storeId]);
    },
    [persist],
  );

  const value = useMemo<SessionContextValue>(() => {
    const activeStores =
      selectedStores.length === 0
        ? bootstrap.stores
        : bootstrap.stores.filter((store) => selectedStores.includes(store.id));

    return {
      user: bootstrap.user,
      stores: bootstrap.stores,
      selectedStores,
      toggleStore,
      selectOnlyStore,
      selectAllStores: () => persist([]),
      storeById: (storeId) => bootstrap.stores.find((store) => store.id === storeId),
      activeStores,
      can: (permission) => bootstrap.user.permissions.includes(permission),
    };
  }, [bootstrap, selectedStores, toggleStore, selectOnlyStore, persist]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession вне SessionProvider');
  return context;
}
